import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * ASN Branded Loading Screen
 * Cycles through brand logos with a glowing circle animation
 */

const LOGOS = [
  {
    src: "https://customer-assets.emergentagent.com/job_1df50573-d6c0-4d1a-b8bf-1707bcf03292/artifacts/4p6t0btc_ASN%20Logo%20Main.png",
    alt: "ASN Logo"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_1df50573-d6c0-4d1a-b8bf-1707bcf03292/artifacts/tfa6yt51_ASN%20Moose.png",
    alt: "ASN Moose"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_1df50573-d6c0-4d1a-b8bf-1707bcf03292/artifacts/vh7wn1dt_TheTeamTV%20White%20Logo.png",
    alt: "The Team TV"
  }
];

export const ASNLoader = ({ size = "default", message = "" }) => {
  const [currentLogo, setCurrentLogo] = useState(0);

  // Cycle through logos every 500ms
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentLogo((prev) => (prev + 1) % LOGOS.length);
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Size configurations
  const sizes = {
    small: { container: "w-16 h-16", logo: "w-10 h-10", ring: 64, stroke: 2 },
    default: { container: "w-24 h-24", logo: "w-16 h-16", ring: 96, stroke: 3 },
    large: { container: "w-32 h-32", logo: "w-20 h-20", ring: 128, stroke: 4 },
    fullscreen: { container: "w-40 h-40", logo: "w-28 h-28", ring: 160, stroke: 5 }
  };

  const config = sizes[size] || sizes.default;

  return (
    <div className="flex flex-col items-center justify-center gap-4">
      <div className={`relative ${config.container}`}>
        {/* Glowing ring animation */}
        <svg
          className="absolute inset-0 w-full h-full"
          viewBox={`0 0 ${config.ring} ${config.ring}`}
        >
          <defs>
            <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="3" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          {/* Background ring */}
          <circle
            cx={config.ring / 2}
            cy={config.ring / 2}
            r={(config.ring / 2) - config.stroke - 4}
            fill="none"
            stroke="rgba(255,255,255,0.1)"
            strokeWidth={config.stroke}
          />
          {/* Animated glowing arc */}
          <motion.circle
            cx={config.ring / 2}
            cy={config.ring / 2}
            r={(config.ring / 2) - config.stroke - 4}
            fill="none"
            stroke="white"
            strokeWidth={config.stroke}
            strokeLinecap="round"
            filter="url(#glow)"
            strokeDasharray={`${(config.ring - config.stroke * 2 - 8) * Math.PI * 0.3} ${(config.ring - config.stroke * 2 - 8) * Math.PI * 0.7}`}
            animate={{
              rotate: [0, 360],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "linear"
            }}
            style={{
              originX: "50%",
              originY: "50%",
              transformOrigin: "center"
            }}
          />
        </svg>

        {/* Logo container with fade animation */}
        <div className="absolute inset-0 flex items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.img
              key={currentLogo}
              src={LOGOS[currentLogo].src}
              alt={LOGOS[currentLogo].alt}
              className={`${config.logo} object-contain`}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.15 }}
            />
          </AnimatePresence>
        </div>
      </div>

      {/* Optional loading message */}
      {message && (
        <motion.p
          className="text-zinc-400 text-sm uppercase tracking-wider"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          {message}
        </motion.p>
      )}
    </div>
  );
};

/**
 * Full-screen loading overlay
 */
export const ASNLoadingScreen = ({ message = "Loading..." }) => {
  return (
    <div className="fixed inset-0 z-50 bg-black flex items-center justify-center">
      <ASNLoader size="fullscreen" message={message} />
    </div>
  );
};

/**
 * Inline loading component for sections
 */
export const ASNLoadingSection = ({ message = "", minHeight = "min-h-[200px]" }) => {
  return (
    <div className={`${minHeight} flex items-center justify-center`}>
      <ASNLoader size="default" message={message} />
    </div>
  );
};

export default ASNLoader;
